import "core-js/modules/es.array.push.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.filter.js";
import "core-js/modules/es.iterator.map.js";
import { ElMessage } from 'element-plus';
import api from '@/api';
import OpticalPathBoard from './OpticalPathBoard.vue';
const SIDES = ['top', 'right', 'bottom', 'left'];
function emptyBoard() {
  return {
    cells: Array.from({
      length: 25
    }, () => null),
    arrows: SIDES.reduce((result, side) => {
      result[side] = Array.from({
        length: 5
      }, () => null);
      return result;
    }, {})
  };
}
function normalizeBoard(value) {
  const board = emptyBoard();
  if (!value || typeof value !== 'object') return board;
  board.cells = Array.from({
    length: 25
  }, (_, index) => value.cells?.[index] || null);
  SIDES.forEach(side => {
    board.arrows[side] = Array.from({
      length: 5
    }, (_, index) => value.arrows?.[side]?.[index] || null);
  });
  return board;
}
function emptyConfig() {
  return {
    version: 1,
    layout: emptyBoard(),
    cases: [emptyBoard()],
    target: emptyBoard(),
    options: [emptyBoard(), emptyBoard(), emptyBoard(), emptyBoard()]
  };
}
function normalizeConfig(value) {
  const source = value && typeof value === 'object' ? value : emptyConfig();
  return {
    version: 1,
    layout: normalizeBoard(source.layout),
    cases: Array.isArray(source.cases) && source.cases.length ? source.cases.slice(0, 8).map(normalizeBoard) : [emptyBoard()],
    target: normalizeBoard(source.target),
    options: Array.from({
      length: 4
    }, (_, index) => normalizeBoard(source.options?.[index]))
  };
}
function parseConfig(value) {
  if (!value) return emptyConfig();
  try {
    return normalizeConfig(typeof value === 'string' ? JSON.parse(value) : value);
  } catch (error) {
    return emptyConfig();
  }
}
function arrowCount(board, direction) {
  return SIDES.reduce((count, side) => count + (board.arrows?.[side] || []).filter(arrow => arrow?.direction === direction).length, 0);
}
export default {
  name: 'OpticalPathQuestionConfigurator',
  components: {
    OpticalPathBoard
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    question: {
      type: Object,
      default: null
    },
    groupId: {
      type: Number,
      default: null
    }
  },
  emits: ['update:visible', 'saved'],
  data() {
    return {
      activeTab: 'layout',
      activeTool: 'filter-red',
      arrowColor: 'red',
      saving: false,
      optionLetters: ['A', 'B', 'C', 'D'],
      elementTools: [{
        value: 'filter-red',
        label: '红色滤光器'
      }, {
        value: 'filter-green',
        label: '绿色滤光器'
      }, {
        value: 'filter-blue',
        label: '蓝色滤光器'
      }, {
        value: 'mirror-forward',
        label: '平面镜 /'
      }, {
        value: 'mirror-backward',
        label: '平面镜 \\'
      }, {
        value: 'splitter',
        label: '分光器'
      }],
      arrowColors: [{
        value: 'red',
        label: '红色光线'
      }, {
        value: 'green',
        label: '绿色光线'
      }, {
        value: 'blue',
        label: '蓝色光线'
      }],
      draft: {
        title: '',
        rightAnswer: 'A',
        analysisText: '',
        opticalConfig: emptyConfig()
      }
    };
  },
  computed: {
    elementCount() {
      return this.draft.opticalConfig.layout.cells.filter(Boolean).length;
    }
  },
  watch: {
    visible(value) {
      if (value) this.resetDraft();
    }
  },
  methods: {
    resetDraft() {
      this.activeTab = 'layout';
      this.activeTool = 'filter-red';
      this.arrowColor = 'red';
      this.draft = {
        title: this.question?.title || '请根据已知案例推测新射入光线的射出路径',
        rightAnswer: this.question?.rightAnswer || 'A',
        analysisText: this.question?.analysisText || '',
        opticalConfig: parseConfig(this.question?.opticalConfig)
      };
    },
    addCase() {
      if (this.draft.opticalConfig.cases.length < 8) {
        this.draft.opticalConfig.cases.push(emptyBoard());
      }
    },
    removeCase(index) {
      this.draft.opticalConfig.cases.splice(index, 1);
    },
    validate() {
      if (!this.draft.title.trim()) return '请输入题干';
      if (this.elementCount === 0) return '请至少放置一个光学元件';
      for (let index = 0; index < this.draft.opticalConfig.cases.length; index++) {
        const board = this.draft.opticalConfig.cases[index];
        if (!arrowCount(board, 'in') || !arrowCount(board, 'out')) {
          return `案例 ${index + 1} 至少需要一条射入光线和一条射出光线`;
        }
      }
      const target = this.draft.opticalConfig.target;
      if (!arrowCount(target, 'in')) return '新射入路径至少需要一条射入光线';
      if (arrowCount(target, 'out')) return '新射入路径只能配置射入光线';
      for (let index = 0; index < 4; index++) {
        const option = this.draft.opticalConfig.options[index];
        if (!arrowCount(option, 'out')) return `选项 ${this.optionLetters[index]} 至少需要一条射出光线`;
        if (arrowCount(option, 'in')) return `选项 ${this.optionLetters[index]} 只能配置射出光线`;
      }
      return null;
    },
    save() {
      const error = this.validate();
      if (error) {
        ElMessage.error(error);
        return;
      }
      this.saving = true;
      api.saveOpticalPathQuestion({
        id: this.question?.id || null,
        groupId: this.groupId || this.question?.questionGroupId,
        title: this.draft.title.trim(),
        rightAnswer: this.draft.rightAnswer,
        analysisText: this.draft.analysisText.trim(),
        opticalConfig: this.draft.opticalConfig
      }).then(() => {
        ElMessage.success('题目已保存');
        this.$emit('update:visible', false);
        this.$emit('saved');
      }).finally(() => {
        this.saving = false;
      });
    }
  }
};